import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useConfirm } from "../../../components/ConfirmDialog";
import { useAuth } from "../../../auth/AuthContext";
import { localizedName } from "../../../lib/localizedName";
import { SUPPORTED_LOCALES } from "../../../i18n";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import { productionStepApi, type ProductionStep } from "../../../api/quality";

/**
 * Šifarnik koraka proizvodnje.
 *
 * Korak je potreban zato što se test ne radi „u procesu" nego na određenom mestu: vlaga posle ekstrakcije
 * i vlaga posle mlevenja su dve propisane obaveze, a plan koji ih ne razlikuje čita dva merenja kao jednu
 * obavezu ispunjenu dvaput.
 *
 * SPISAK JE BANKOMOV, NE PRENOS IZ ERP-a, i ekran to kaže. Stari ERP korake proizvodnje ne vodi: nema
 * tabele faza, operacija, linija ni mašina, a polje operacije na recepturi je popunjeno na 17 od 10.490
 * receptura. Jedino mesto gde je proizvodnja podeljena na imenovane celine je registar opreme, i četiri
 * koraka koja su upisana dolaze odatle. Tostiranje, peletiranje i teksturiranje se vide u nazivima
 * proizvoda a nemaju svoj pogon, pa ih u spisku nema dok ih kvalitet ne doda — spisak koji izgleda potpun
 * a nije gori je od spiska koji otvoreno nije potpun.
 */
export default function ProductionStepsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  // Koraci su deo konfiguratora, pa ih vodi onaj ko vodi i planove (KK5). Laborant ih čita kroz plan.
  const smeDaVodi = hasPermission("production_steps:manage");
  const canDelete = hasPermission("section:production_steps:full");

  const [rows, setRows] = useState<ProductionStep[] | null>(null);
  const [editing, setEditing] = useState<ProductionStep | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await productionStepApi.list(false));
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Povlačenje koraka. Nikad brisanje: propisane stavke i nalazi pokazuju na njega, a nalaz koji kaže
   * „izmereno na ekstrakciji" mora to da kaže i posle reorganizacije pogona.
   */
  async function retire(s: ProductionStep) {
    const ok = await confirm({
      message:
        s.timesPrescribed > 0
          ? t(
              "steps.confirmRetireUsed",
              "Korak „{{korak}}“ stoji na {{n}} propisanih testova. Povlačenjem izlazi iz novih planova, a upisani nalazi ostaju vezani za njega. Da ga povučem?",
              { korak: s.code, n: s.timesPrescribed },
            )
          : t("steps.confirmRetire", "Da povučem korak „{{korak}}“ iz upotrebe?", { korak: s.code }),
      danger: true,
    });
    if (!ok) return;
    try {
      await productionStepApi.retire(s.id);
      toast.success(t("steps.retired", "Korak je povučen iz upotrebe."));
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("steps.title", "Koraci proizvodnje")}</h1>
          <div className="sub">
            {t(
              "steps.lead",
              "Mesta kroz koja soja prolazi, da bi se znalo gde se koji test radi: vlaga posle ekstrakcije i vlaga posle mlevenja su dva propisana testa, ne jedan.",
            )}
          </div>
        </div>
        {smeDaVodi && !creating && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              <Ic.plus /> {t("steps.new", "Nov korak")}
            </button>
          </div>
        )}
      </div>

      {/* Odakle spisak dolazi stoji na ekranu, a ne u dokumentu koji korisnik nema. */}
      <div className="card">
        <div className="card-b">
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
            {t(
              "steps.origin",
              "Ovaj spisak portal vodi sam. Stari ERP korake proizvodnje ne vodi — nema tabele faza, operacija ni linija, a polje operacije na recepturi je popunjeno na 17 od 10.490 receptura. Upisana četiri koraka dolaze iz registra opreme starog ERP-a i treba ih potvrditi.",
            )}
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--ink-2)" }}>
            {t(
              "steps.missing",
              "Tostiranje, peletiranje i teksturiranje se vide u nazivima proizvoda, ali nemaju svoj pogon ni dokument, pa ih u spisku nema. Dodaju se ovde kad kvalitet odredi gde se mere.",
            )}
          </p>
        </div>
      </div>

      {smeDaVodi && (creating || editing) && (
        <StepForm
          step={editing}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await load();
          }}
        />
      )}

      {rows === null ? (
        <PanelLoading text={t("steps.loading", "Čitam šifarnik…")} />
      ) : rows.length === 0 ? (
        <div className="empty">
          {t("steps.none", "Šifarnik je prazan. Upišite korake kroz koje proizvodnja ide.")}
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("steps.col.step", "Korak")}</th>
              <th>{t("steps.col.order", "Redosled")}</th>
              <th>{t("steps.col.prescribed", "Propisanih testova")}</th>
              <th>{t("steps.col.state", "Stanje")}</th>
              {smeDaVodi && <th aria-label={t("common.actions", "Akcije")} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{localizedName({ name: s.code, nameI18n: s.nameI18n }, lang)}</strong>
                  <div style={{ ...tiho, fontFamily: "var(--mono)" }}>{s.code}</div>
                  {s.note && <div className="muted">{s.note}</div>}
                </td>
                <td style={{ fontVariantNumeric: "tabular-nums" }}>{s.sortOrder}</td>
                <td style={{ fontVariantNumeric: "tabular-nums" }}>
                  {s.timesPrescribed > 0 ? (
                    s.timesPrescribed
                  ) : (
                    <span className="tag neutral">{t("steps.notPrescribed", "nijedan")}</span>
                  )}
                </td>
                <td>
                  <span className={`tag ${s.active ? "ok" : "neutral"}`}>
                    {s.active ? t("steps.active", "u upotrebi") : t("steps.retiredTag", "povučen")}
                  </span>
                </td>
                {smeDaVodi && (
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="btn ghost sm" onClick={() => setEditing(s)}>
                      {t("common.edit", "Uredi")}
                    </button>
                    {s.active && (
                      <button type="button" className="btn ghost sm" disabled={!canDelete} onClick={() => void retire(s)}>
                        {t("steps.retire", "Povuci")}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Obrazac koraka.
 *
 * Šifra se posle upisa ne menja: propisane stavke i nalazi pokazuju na korak, a promenjena šifra čini
 * istoriju merenja nečitljivom. Ispravlja se prikazano ime, ne šifra.
 */
function StepForm({
  step,
  onSaved,
  onCancel,
}: {
  step: ProductionStep | null;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [code, setCode] = useState(step?.code ?? "");
  const [names, setNames] = useState<Record<string, string>>(step?.nameI18n ?? {});
  const [sortOrder, setSortOrder] = useState(step ? String(step.sortOrder) : "");
  const [note, setNote] = useState(step?.note ?? "");
  const [active, setActive] = useState(step?.active ?? true);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Redosled koji nije broj se ne pretvara tiho u „na kraj lanca": to bi korak gurnulo iza pripreme
    // bez reči.
    const red = sortOrder.trim() === "" ? undefined : Number(sortOrder);
    if (red !== undefined && !Number.isInteger(red)) {
      toast.error(t("steps.form.orderNotNumber", "Redosled je ceo broj, ili ostaje prazno."));
      return;
    }
    setBusy(true);
    try {
      if (step) {
        await productionStepApi.update(step.id, {
          nameI18n: names,
          sortOrder: red,
          active,
          note: note.trim(),
        });
      } else {
        await productionStepApi.create({
          code: code.trim().toUpperCase(),
          nameI18n: names,
          sortOrder: red,
          note: note.trim(),
        });
      }
      toast.success(t("steps.saved", "Korak je sačuvan."));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-h">
        <strong>{step ? t("steps.editTitle", "Uređivanje koraka") : t("steps.new", "Nov korak")}</strong>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={mreza}>
          <label style={polje}>
            <span style={tiho}>{t("steps.form.code", "Šifra")}</span>
            <input
              className="input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={!!step}
              required
              pattern="[A-Z][A-Z0-9_-]*"
            />
            <span style={{ ...tiho, fontSize: 11 }}>
              {step
                ? t("steps.form.codeImmutable", "Šifra se ne menja — propisani testovi i nalazi pokazuju na nju.")
                : t("steps.form.codeHint", "Velika slova, cifre i crta, na primer TOSTIRANJE.")}
            </span>
          </label>
          <label style={polje}>
            <span style={tiho}>{t("steps.form.order", "Redosled")}</span>
            <input className="input" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            <span style={{ ...tiho, fontSize: 11 }}>
              {t("steps.form.orderHint", "Prazno znači na kraj lanca. Brojevi idu po deset, da korak može između.")}
            </span>
          </label>
        </div>

        <label style={polje}>
          <span style={tiho}>{t("steps.form.note", "Napomena")}</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        <div style={polje}>
          <span style={tiho}>{t("steps.form.names", "Naziv, po jeziku")}</span>
          <div style={mreza}>
            {SUPPORTED_LOCALES.map((loc) => (
              <label key={loc} style={polje}>
                <span style={{ ...tiho, fontSize: 11 }}>{loc}</span>
                <input
                  className="input"
                  value={names[loc] ?? ""}
                  onChange={(e) => setNames((prev) => ({ ...prev, [loc]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        </div>

        {step && (
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>{t("steps.form.active", "U upotrebi")}</span>
          </label>
        )}

        <div className="page-actions">
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? t("common.saving", "Čuvam…") : t("common.save", "Sačuvaj")}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            {t("common.cancel", "Odustani")}
          </button>
        </div>
      </div>
    </form>
  );
}

const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };

const polje: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };

const mreza: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: 12,
};
